import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CaretDoubleRight, X, Database } from "@phosphor-icons/react";
import { setShowProfileMemoryPanel, fetchProfileMemory } from "src/store/slices/profileMemorySlice";
import { trackAnalytics } from "src/pages/helpers";
import { EVENT_NAMES, EVENT_PARAMETERS, MODAL_NAMES, getPageName } from "src/utils/EventConstants";
import PersonalSection from "./PersonalSection";
import BusinessSection from "./BusinessSection";
import RelatedEntitySection from "./RelatedEntitySection";
import MemoryIntroBanner from "./MemoryIntroBanner";
import MemorySkeleton from "./MemorySkeleton";

const ProfileMemoryShelf = () => {
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const showPanel = useSelector((state) => state.profileMemory.showPanel);
  const fetchStatus = useSelector((state) => state.profileMemory.fetchStatus);
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);

  useEffect(() => {
    if (showPanel) {
      dispatch(fetchProfileMemory());
      requestAnimationFrame(() => setIsVisible(true));
    }
  }, [showPanel, dispatch]);

  const hidePanel = () => {
    trackAnalytics(
      EVENT_NAMES.CLOSED_MODAL,
      {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.MEMORY_SHELF,
      },
      userMetadata,
    );
    setIsVisible(false);
    setTimeout(() => {
      dispatch(setShowProfileMemoryPanel(false));
    }, 300);
  };

  if (!showPanel) return null;

  return (
    <div
      className={`fixed inset-0 z-[1100] transition-opacity duration-300 ${isVisible ? "bg-[rgba(0,0,0,0.5)]" : "bg-transparent"}`}
      onMouseDown={hidePanel}
    >
      <aside
        data-testid="profile-memory-drawer"
        className={`flex flex-col bg-white shadow-xl absolute z-[1200] right-0 h-[92%] top-[8%] w-full rounded-t-[20px] md:h-screen md:top-0 md:w-[520px] md:rounded-none md:border-l md:border-l-[#E4E4E4] transition-transform duration-300 ease-out ${isVisible ? "translate-y-0 md:translate-x-0" : "translate-y-full md:translate-y-0 md:translate-x-full"
          }`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0F0F0] shrink-0">
          <div className="flex items-center gap-2">
            <Database size={18} className="text-[#1B2B48]" />
            <h2 className="text-[16px] font-bold text-[#1B2B48]">Memory</h2>
          </div>
          <button
            data-testid="profile-memory-close"
            className="flex items-center text-sm cursor-pointer font-semibold text-[#353535]"
            onClick={hidePanel}
          >
            <X className="md:hidden" size={16} />
            <CaretDoubleRight className="hidden md:block" size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {fetchStatus === "loading" ? (
            <MemorySkeleton />
          ) : (
            <div className="flex flex-col gap-8">
              <MemoryIntroBanner />
              <PersonalSection />
              <BusinessSection />
              <RelatedEntitySection />
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default ProfileMemoryShelf;
